/**
 * Configuration for connection-specific field requirements in leads criteria forms
 * This config determines which fields are optional/required for specific connections
 */

export interface ConnectionFieldRequirements {
  loanTypes: boolean;
  loanAmountRange: boolean;
  areaTypes: boolean;
}

/**
 * Default requirements - all fields are required
 */
const DEFAULT_REQUIREMENTS: ConnectionFieldRequirements = {
  loanTypes: true,
  loanAmountRange: true,
  areaTypes: true,
};

/**
 * Connection-specific field requirements
 * If a connection is not listed, DEFAULT_REQUIREMENTS will be used
 */
const CONNECTION_REQUIREMENTS: Record<
  string,
  Partial<ConnectionFieldRequirements>
> = {
  'dh::nc-sbtdc-ssbci': {
    loanTypes: false,
    loanAmountRange: false,
    areaTypes: false,
  },
};

/**
 * Get field requirements for a specific connection
 * @param connectionName - The connection name (e.g., 'dh::nc-sbtdc-ssbci')
 * @returns Field requirements object with boolean flags for each field
 */
export function getFieldRequirements(
  connectionName?: string | null,
): ConnectionFieldRequirements {
  if (!connectionName) {
    return DEFAULT_REQUIREMENTS;
  }

  const connectionConfig = CONNECTION_REQUIREMENTS[connectionName];
  if (!connectionConfig) {
    return DEFAULT_REQUIREMENTS;
  }

  // Merge connection-specific config with defaults
  return {
    ...DEFAULT_REQUIREMENTS,
    ...connectionConfig,
  };
}

/**
 * Check if a specific field is required for a connection
 * @param connectionName - The connection name
 * @param fieldName - The field name to check
 * @returns true if field is required, false if optional
 */
export function isFieldRequired(
  connectionName: string | undefined | null,
  fieldName: keyof ConnectionFieldRequirements,
): boolean {
  const requirements = getFieldRequirements(connectionName);
  return requirements[fieldName];
}
